import { useState, useEffect, useRef, useCallback } from 'react'

export interface UseCameraOptions {
  deviceId?: string
  width?: number
  height?: number
  frameRate?: number
  audioDeviceId?: string
}

export function useCamera(options: UseCameraOptions = {}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const captureVideoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [isWaitingForPermission, setIsWaitingForPermission] = useState(false)

  // Ensure we have a hidden video element for capturing that stays mounted
  useEffect(() => {
    const video = document.createElement('video')
    video.autoplay = true
    video.playsInline = true
    video.muted = true
    // Some browsers don't render/play video if display is none
    video.style.position = 'fixed'
    video.style.top = '0'
    video.style.left = '0'
    video.style.width = `${options.width || 1920}px`
    video.style.height = `${options.height || 1080}px`
    video.style.opacity = '0'
    video.style.pointerEvents = 'none'
    document.body.appendChild(video)
    captureVideoRef.current = video
    return () => {
      document.body.removeChild(video)
    }
  }, [options.width, options.height])

  const enumerate = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      setDevices(devs.filter((d) => d.kind === 'videoinput'))
    } catch {
      setDevices([])
    }
  }, [])

  const attach = useCallback((videoEl?: HTMLVideoElement | null) => {
    const target = videoEl ?? videoRef.current
    if (!target || !streamRef.current) return
    target.srcObject = streamRef.current
    target.onloadedmetadata = () => setReady(true)
    if (target.readyState >= 1) setReady(true)
  }, [])

  const start = useCallback(async () => {
    setError(null)
    const timer = setTimeout(() => setIsWaitingForPermission(true), 2000)
    
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'camera' as PermissionName }).then(status => {
        if (status.state === 'prompt') {
          setIsWaitingForPermission(true)
        }
      }).catch(() => {})
    }
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
      }
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: options.deviceId ? { exact: options.deviceId } : undefined,
          width: { ideal: options.width || 1920 },
          height: { ideal: options.height || 1080 },
          frameRate: { ideal: options.frameRate || 30 },
        },
        audio: options.audioDeviceId ? { deviceId: { exact: options.audioDeviceId } } : true,
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      clearTimeout(timer)
      setIsWaitingForPermission(false)
      streamRef.current = stream
      
      // Always attach to our hidden capture video
      if (captureVideoRef.current) {
        captureVideoRef.current.srcObject = stream
        captureVideoRef.current.play().catch(() => {})
      }
      
      attach()
      await enumerate()
    } catch (err) {
      clearTimeout(timer)
      setIsWaitingForPermission(false)
      setError(err instanceof Error ? err.message : 'Camera failed')
    }
  }, [options.deviceId, options.width, options.height, options.frameRate, options.audioDeviceId, enumerate, attach])

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (captureVideoRef.current) captureVideoRef.current.srcObject = null
    setReady(false)
  }, [])

  const capture = useCallback((): string | null => {
    // Prefer the persistent capture video element
    const video = captureVideoRef.current || videoRef.current
    if (!video || video.readyState < 2 || video.videoWidth === 0) return null
    
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    
    ctx.drawImage(video, 0, 0)
    
    // Safety check: ensure the frame isn't just pure black
    // Some cameras/browsers report readyState 2 but still output black for a few frames
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    let isBlack = true
    // Sample a few pixels to see if any have color
    for (let i = 0; i < pixels.length; i += 1024) {
      if (pixels[i] > 10 || pixels[i+1] > 10 || pixels[i+2] > 10) {
        isBlack = false
        break
      }
    }
    
    if (isBlack) return null

    return canvas.toDataURL('image/jpeg', 0.92)
  }, [])
 
  const startRecording = useCallback(() => {
    if (!streamRef.current) return
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      console.warn('Recording already in progress')
      return
    }
    chunksRef.current = []
    
    let options = { mimeType: 'video/webm;codecs=vp8' }
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      console.warn('vp8 not supported, trying default webm')
      options = { mimeType: 'video/webm' }
    }
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      console.warn('webm not supported, trying mp4')
      options = { mimeType: 'video/mp4' }
    }

    try {
      const recorder = new MediaRecorder(streamRef.current, options)
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }
      recorder.start(1000) // capture in 1s slices to be safer
      recorderRef.current = recorder
      console.log('Started recording with', options.mimeType)
    } catch (err) {
      console.error('Failed to start MediaRecorder', err)
    }
  }, [])
 
  const stopRecording = useCallback(() => {
    return new Promise<void>((resolve) => {
      if (!recorderRef.current || recorderRef.current.state === 'inactive') {
        resolve()
        return
      }
      recorderRef.current.onstop = () => {
        recorderRef.current = null
        resolve()
      }
      recorderRef.current.stop()
    })
  }, [])
 
  const requestVideoBlob = useCallback((): Blob | null => {
    if (chunksRef.current.length === 0) return null
    const type = recorderRef.current?.mimeType || 'video/webm'
    return new Blob(chunksRef.current, { type })
  }, [])

  useEffect(() => {
    enumerate()
    return () => stop()
  }, [enumerate, stop])

  return { videoRef, captureVideoRef, start, stop, capture, attach, ready, error, devices, stream: streamRef.current, startRecording, stopRecording, requestVideoBlob, isWaitingForPermission }
}
